import { AgBarSeriesItemStylerParams, AgBarSeriesStyle, AgSparklineOptions, Styler } from 'ag-charts-community'

import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { createStatusCellRenderer } from '@fortem-monorepo/react-ag'
import { Posmon } from '@fortem-monorepo/web-sdk'

export const investmentWrappersLs = ['EIS', 'NOTE', 'EMTN', 'WARRANT', 'N/A']

const styling_rules = { light_neg_bg: 'x==8', light_mid_bg: 'x==0', light_pos_bg: 'x==2' }
const red_green_rules = { 'bg-red-200': 'x==0', 'bg-green-200': 'x==1' }

const palette = {
  blue: 'rgb(20,94,140)',
  lightBlue: 'rgb(182,219,242)',
  red: '#f44336',
  lightRed: '#ea9999',
  green: 'rgb(63,141,119)',
  lightGreen: 'rgb(124,218,124)',
}

const barStyler: Styler<AgBarSeriesItemStylerParams<any, any>, AgBarSeriesStyle> = (params) => {
  const { yValue, highlighted } = params

  if (highlighted) {
    return
  }

  let color = palette.lightGreen

  if (-100 <= yValue && yValue <= -50) {
    color = palette.red
  } else if (-50 < yValue && yValue <= 0) {
    color = palette.lightRed
  } else if (0 < yValue && yValue <= 50) {
    color = palette.lightGreen
  } else if (50 < yValue) {
    color = palette.green
  }

  return { fill: color }
}

export const createflowMonitorColDefs = (canEdit: boolean, guarantors: any[], issuers: any[]) => {
  return createColDefs<Posmon.GetSummaryData[number]>([
    {
      field: 'isin',
      width: 115,
      cellStyle: { textAlign: 'center' },
      pinned: 'left',
      cellClassRules: {
        'bg-red-200': (params) => {
          return (
            !!params.data?.days_to_maturity && 0 <= params.data?.days_to_maturity && params.data?.days_to_maturity <= 10
          )
        },
        'bg-amber-200': (params) => {
          return (
            !!params.data?.days_to_maturity &&
            11 <= params.data?.days_to_maturity &&
            params.data?.days_to_maturity <= 20
          )
        },
        'bg-green-200': (params) => {
          return (
            !!params.data?.days_to_maturity &&
            21 <= params.data?.days_to_maturity &&
            params.data?.days_to_maturity <= 30
          )
        },
      },
    },
    {
      headerName: 'Dates',
      groupId: 'dates',
      children: [
        {
          field: 'strike_date',
          headerName: 'Strike Dt',
          width: 100,
          cellStyle: { textAlign: 'center' },
          wrapHeaderText: true,
          autoHeaderHeight: true,
        },
        {
          field: 'maturity_date',
          headerName: 'Maturity Dt',
          width: 100,
          cellStyle: { textAlign: 'center' },
          wrapHeaderText: true,
          autoHeaderHeight: true,
          columnGroupShow: 'open',
        },
        {
          field: 'days_to_maturity',
          headerName: '#Days To Maturity',
          width: 100,
          hide: false,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          columnGroupShow: 'open',
        },
        {
          field: 'expiry_date',
          headerName: 'Expiry Dt',
          width: 100,
          cellStyle: { textAlign: 'center' },
          cellEditor: 'agDateStringCellEditor',
          editable: canEdit,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          columnGroupShow: 'open',
        },
        {
          field: 'expired',
          width: 90,
          editable: canEdit,
          cellStyle: { textAlign: 'center' },
          suppressHeaderFilterButton: true,
          suppressFloatingFilterButton: true,
        },
      ],
    },
    {
      headerName: 'Config',
      groupId: 'Config',
      children: [
        {
          field: 'instrument_type',
          headerName: 'Type',
          width: 125,
        },
        {
          field: 'currency',
          headerName: 'Ccy',
          width: 95,
          cellStyle: { textAlign: 'center' },
        },
        {
          field: 'coupon_annual_pct',
          headerName: 'Coupon Yearly %',
          editable: canEdit,
          cellStyle: { textAlign: 'center' },
          wrapHeaderText: true,
          autoHeaderHeight: true,
          width: 100,
        },
        {
          field: 'description',
          width: 400,
          columnGroupShow: 'open',
        },
        {
          field: 'investment_wrapper',
          headerName: 'Wrapper',
          width: 100,
          editable: canEdit,
          cellEditorParams: { values: investmentWrappersLs },
          cellEditor: 'agSelectCellEditor',
          cellStyle: { textAlign: 'center' },
          columnGroupShow: 'open',
        },
        {
          field: 'calculation_type',
          headerName: 'Calc Type',
          width: 100,
          cellStyle: { textAlign: 'center' },
          columnGroupShow: 'open',
        },
        {
          field: 'autocall_observation_per_year',
          headerName: 'Autocall Yearly Obs',
          cellStyle: { textAlign: 'center' },
          width: 100,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          columnGroupShow: 'open',
        },
        {
          field: 'payoff_observation_per_year',
          headerName: 'Payoff Yearly Obs',
          cellStyle: { textAlign: 'center' },
          width: 100,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          columnGroupShow: 'open',
        },
      ],
    },
    {
      headerName: 'Issuer',
      groupId: 'Issuer',
      children: [
        {
          field: 'issuer_short_name',
          headerName: 'Issuer',
          width: 175,
        },
        {
          field: 'issuer_long_name',
          headerName: 'Issuer Long',
          editable: canEdit,
          cellEditorParams: { values: issuers },
          cellEditor: 'agSelectCellEditor',
          width: 300,
          columnGroupShow: 'open',
        },
        {
          field: 'guarantor_name',
          headerName: 'Guarantor',
          editable: canEdit,
          cellEditorParams: { values: guarantors },
          cellEditor: 'agSelectCellEditor',
          width: 200,
          columnGroupShow: 'open',
        },
        {
          field: 'guarantor_ratings',
          headerName: 'Ratings',
          width: 250,
          editable: canEdit,
          columnGroupShow: 'open',
        },
        {
          field: 'venue_listing',
          headerName: 'Venue',
          width: 125,
          editable: canEdit,
          columnGroupShow: 'open',
        },
      ],
    },
    {
      headerName: 'Smartrade',
      groupId: 'Smartrade',
      children: [
        {
          field: 'on_smartrade',
          headerName: 'Smartrade',
          width: 130,
          cellRenderer: smartradeCellRenderer,
        },
        {
          field: 'trade_number',
          headerName: '#Trades',
          cellStyle: { textAlign: 'center' },
          width: 100,
          columnGroupShow: 'open',
          valueFormatter: numberFormatter({
            delimiter: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
        },
        {
          field: 'smartrade_trade_proportion',
          headerName: 'Proportion',
          columnGroupShow: 'open',
          cellClass: ['flex items-center w-full h-full'],
          width: 200,
          minWidth: 200,
          cellRenderer: 'agSparklineCellRenderer',
          cellRendererParams: {
            sparklineOptions: {
              type: 'bar',
              direction: 'horizontal',
              yKey: 'proportion',
              min: -100,
              max: 100,
              label: {
                enabled: true,
                placement: 'outside-end',
                fontWeight: 'bold',
                fontSize: 11,
                formatter: function (params: any) {
                  return `${params.value}%`
                },
              },
              padding: {
                top: 5,
                bottom: 5,
                left: 40,
                right: 40,
              },
              itemStyler: barStyler,
            } satisfies AgSparklineOptions<Posmon.GetSummaryData[number]>,
          },
        },
        {
          field: 'volume',
          headerName: 'Volume',
          type: 'rightAligned',
          width: 115,
          columnGroupShow: 'open',
          valueFormatter: numberFormatter({
            delimiter: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }),
        },

        {
          field: 'setup_to_fix',
          headerName: 'ToFix',
          width: 83,
          columnGroupShow: 'open',
          cellClassRules: red_green_rules,
          valueFormatter: () => '',
        },
      ],
    },
    {
      headerName: 'Stresstest',
      groupId: 'Stresstest',
      children: [
        {
          field: 'has_all',
          headerName: 'Ready',
          width: 130,
          cellRenderer: stresstestCellRenderer,
        },
        {
          field: 'risk_date',
          headerName: 'Date',
          width: 100,
          columnGroupShow: 'open',
          // cellRenderer: stresstestCellRenderer,
        },
        {
          field: 'mv_base',
          headerName: 'Scenario',
          width: 120,
          columnGroupShow: 'open',
          cellRenderer: stresstestCellRenderer,
        },
        {
          field: 'risk_base',
          headerName: 'Risk',
          width: 100,
          columnGroupShow: 'open',
          cellRenderer: stresstestCellRenderer,
        },
      ],
    },
    {
      headerName: 'Marketing',
      groupId: 'Website',
      children: [
        {
          field: 'include_on_website',
          headerName: 'On Website',
          editable: canEdit,
          width: 130,
          cellRenderer: websiteCellRenderer,
        },
        {
          field: 'priority_ranking',
          headerName: 'Priority Ranking',
          columnGroupShow: 'open',
          editable: canEdit,
        },
        {
          field: 'region',
          headerName: 'Region',
          columnGroupShow: 'open',
          editable: canEdit,
        },
        {
          field: 'asset',
          headerName: 'Asset',
          columnGroupShow: 'open',
          editable: canEdit,
        },
        {
          field: 'access',
          headerName: 'Access',
          columnGroupShow: 'open',
          editable: canEdit,
        },
        {
          field: 'on_secondary_email',
          headerName: 'On Secondary Email',
          width: 100,
          columnGroupShow: 'open',
          editable: canEdit,
        },
        {
          field: 'inventory',
          headerName: 'Inventory',
          width: 100,
          columnGroupShow: 'open',
          editable: canEdit,
        },
      ],
    },
  ])
}

export const tradeblotterColDefs = createColDefs([
  {
    field: 'trade_date',
    headerName: 'Date',
    width: 97,
  },
  {
    field: 'orderqty',
    headerName: 'Qty',
    width: 90,
    type: 'rightAligned',
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  },
  {
    field: 'price',
    width: 90,
    type: 'rightAligned',
    valueFormatter: numberFormatter({ delimiter: true }),
  },
  {
    headerName: 'Addls',
    groupId: 'Addls',
    children: [
      {
        field: 'ordstatus',
        headerName: 'Status',
        width: 82,
        cellClassRules: styling_rules,
      },
      {
        field: 'notional',
        width: 110,
        type: 'rightAligned',
        valueFormatter: numberFormatter({
          delimiter: true,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
        columnGroupShow: 'open',
      },
      {
        field: 'clordid',
        width: 115,
        columnGroupShow: 'open',
      },
      {
        field: 'ordtype',
        width: 100,
        columnGroupShow: 'open',
      },
      {
        field: 'tofix',
        headerName: 'Setup To Fix',
        width: 115,
        columnGroupShow: 'open',
      },
      {
        field: 'trade_date_tsp',
        headerName: 'Trade Tsp',
        width: 170,
        columnGroupShow: 'open',
      },
    ],
  },
])

export const scheduleColDefs = createColDefs([
  {
    field: 'data_type',
    headerName: 'isin',
    width: 110,
    rowGroup: true,
    hide: true,
  },
  {
    field: 'startobservationdate',
    headerName: 'Obs Date',
    width: 97,
  },
  {
    field: 'initobslvl',
    headerName: 'Price Ref',
    width: 100,
    valueFormatter: numberFormatter({
      delimiter: true,
    }),
  },
  {
    field: 'payoffbarrierlvlpercent',
    headerName: 'Payoff Lvl%',
    width: 95,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  },
  {
    field: 'autocallbarrierlvlpercent',
    headerName: 'Autocall Lvl%',
    width: 95,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  },
  {
    field: 'couponrate',
    headerName: 'Coupon%',
    width: 90,
  },
  {
    field: 'strike',
    headerName: 'Strike',
    width: 90,
    valueFormatter: numberFormatter({
      delimiter: true,
    }),
  },
  {
    field: 'strikepercent',
    headerName: 'Strike%',
    width: 90,
  },
  {
    field: 'participationrate',
    headerName: 'Side',
    width: 75,
  },
  {
    field: 'instrumentticker',
    headerName: 'Index',
    width: 97,
  },
  {
    field: 'optionclusterid',
    headerName: 'Clster ID',
    width: 87,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  },
])

export const createHistoricalPricesColDefs = () => {
  return createColDefs([
    {
      field: 'instrument_id',
      hide: true,
    },
    {
      field: 'date',
      width: 100,
    },
    {
      field: 'isin',
      width: 110,
    },
    {
      field: 'price',
      width: 95,
    },
  ])
}

const stresstestCellRenderer = createStatusCellRenderer({
  success: { label: 'Ready', values: [1] },
  error: { label: 'Missing', values: [0] },
})

const websiteCellRenderer = createStatusCellRenderer({
  success: { label: 'Displayed', values: [true] },
  error: { label: 'Not displayed', values: [0, false, null, undefined] },
})

const smartradeCellRenderer = createStatusCellRenderer({
  success: { label: 'Present', values: [1] },
  error: { label: 'Missing', values: [0] },
})
